import React, { useContext } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, Link } from '@material-ui/core';

import {
  BookLocationModifiersContext,
  BookLocationValuesContext,
} from 'contexts';

const useStyles = makeStyles((theme) => {
  return {
    root: {},
    hidden: {
      display: 'none',
    },
  };
});

const NavigateToLocation = ({
  children,
  bookLocation,
  linkLabel,
  id,
  onClick,
  onSuccessfulNavigate,
  ...props
}) => {
  const classes = useStyles();

  const { goToLocation } = useContext(BookLocationModifiersContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);

  if (!bookLocation || !bookLocation.book) {
    console.warn(
      'No bookLocation passed to NavigateToLocation, falling back on currentBookLocation',
    );
    bookLocation = currentBookLocation;
  }

  // Support calling whatever the consumer set as onClick in addition to navigating to a new location.
  props.onClick = async (event) => {
    event.preventDefault();
    onClick?.(event);

    const currentLocation = await goToLocation(bookLocation);
    const currentDocumentIndex = currentLocation?.document?.index;
    const isCorrectDocument =
      bookLocation.document.index === currentDocumentIndex;

    if (isCorrectDocument) {
      onSuccessfulNavigate?.(event);
    }
  };

  if (props.component !== 'button') {
    // Additional safety check before accessing href
    try {
      props.href = bookLocation.href;
    } catch (error) {
      console.error(
        'NavigateToLocation: Error accessing bookLocation.href:',
        error,
        { bookLocation },
      );
      props.href = '#'; // fallback to prevent broken links
    }
  }

  return (
    <>
      {linkLabel && typeof linkLabel !== 'string' ? (
        <div className={classes.hidden} id={`link-Label-${id}`}>
          {linkLabel}
        </div>
      ) : null}

      <Link
        className={clsx(classes.root, 'noTranslate')}
        {...(!linkLabel
          ? {} // no link label, no aria
          : typeof linkLabel === 'string'
            ? { 'aria-label': linkLabel } // link label is a string, use it as ariaLabel
            : { 'aria-labelledby': `link-Label-${id}` })} // link label is a node, use it as ariaLabelledby
        {...props}
      >
        {children}
      </Link>
    </>
  );
};

NavigateToLocation.propTypes = {
  /**
   * The content to be passed down into the link/button.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  /**
   * A BookLocation object to use as the navigation target.
   * If it is passed in, all other location properties will be ignored.
   */
  bookLocation: PropTypes.object,
  /**
   * For convenience, if any or some these location properties are passed in,
   * the component will navigate to a new location with these parameter(s) relative to the current location.
   *
   * For example, passing in just a cfi will go to that cfi in the current document.
   * Passing in a page will go to that page.
   */
  document: PropTypes.object,
  documentNumber: PropTypes.number,
  documentIndex: PropTypes.number,
  linkLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  marker: PropTypes.object,
  page: PropTypes.object,
  cfi: PropTypes.string,
  element: PropTypes.string,
  /**
   * All nonspecified properties will be passed to the Link component, such as styling.
   * Pass component="button" to render as a button.
   */
  props: PropTypes.object,
  /**
   * A function to call when the user clicks the link/button.
   */
  onClick: PropTypes.func,
  /**
   * A function to call when the navigation is successful.
   */
  onSuccessfulNavigate: PropTypes.func,
};

export default NavigateToLocation;
